import { deepCopy } from '@ethersproject/properties'
import { StaticJsonRpcProvider } from '@ethersproject/providers'
import { isPlain } from '@reduxjs/toolkit'
import { ChainId } from 'toonswap-sdk'

export const rpcMap = {
  [ChainId.MAINNET]: process.env.REACT_APP_NODE_1 || 'https://ethereum-rpc.publicnode.com',
  [ChainId.BSCTESTNET]: 'https://data-seed-prebsc-1-s1.bnbchain.org:8545',
}

const AVERAGE_L1_BLOCK_TIME = 12000
const CHAIN_IDS_TO_NAMES = {
  [ChainId.MAINNET]: 'mainnet',
  [ChainId.BSCTESTNET]: 'tsupra',
}

class AppJsonRpcProvider extends StaticJsonRpcProvider {
  private _blockCache = new Map<string, Promise<any>>()

  get blockCache() {
    if (!this._blockCache.size) {
      this.once('block', () => this._blockCache.clear())
    }
    return this._blockCache
  }

  constructor(chainId: ChainId) {
    super(rpcMap[chainId], {
      chainId,
      name: CHAIN_IDS_TO_NAMES[chainId],
    })

    this.pollingInterval = AVERAGE_L1_BLOCK_TIME
  }

  send(method: string, params: Array<any>): Promise<any> {
    // Only cache eth_call's.
    if (method !== 'eth_call') return super.send(method, params)

    // Only cache if params are serializable.
    if (!isPlain(params)) return super.send(method, params)

    const key = `call:${JSON.stringify(params)}`
    const cached = this.blockCache.get(key)
    if (cached) {
      this.emit('debug', {
        action: 'request',
        request: deepCopy({ method, params, id: 'cache' }),
        provider: this,
      })
      return cached
    }

    const result = super.send(method, params)
    this.blockCache.set(key, result)
    return result
  }
}

/**
 * These are the only JsonRpcProviders used directly by the interface.
 */
export const RPC_PROVIDERS: {
  [key in ChainId]: StaticJsonRpcProvider
} = {
  [ChainId.MAINNET]: new AppJsonRpcProvider(ChainId.MAINNET),
  [ChainId.BSCTESTNET]: new AppJsonRpcProvider(ChainId.BSCTESTNET),
}
